// Apollo Client with Multiple Endpoint Support
// 
// Usage:
// import { createApolloClientForEndpoint } from './index';
// 
// // Create client for main endpoint (production)
// const mainClient = createApolloClientForEndpoint(auth, 'main');
// 
// // Create client for secondary endpoint (DDN)
// const secondaryClient = createApolloClientForEndpoint(auth, 'secondary');
// 
// // Use in queries/mutations
// const { data } = useQuery(YOUR_QUERY, { client: mainClient });

import {
  ApolloLink,
  createHttpLink,
  from,
  RequestHandler,
  split,
  WatchQueryFetchPolicy,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { createRestartableClient } from './ws';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { KaktuAuthClient } from '../../auth';

const isBrowser = typeof window !== 'undefined';

export type KaktuApolloClientOptions = {
  auth: KaktuAuthClient;
  graphqlUrl?: string;
  headers?: any;
  fetchPolicy?: WatchQueryFetchPolicy;
  connectToDevTools?: boolean;
  cache?: InMemoryCache;
  /**
   * @deprecated Please use `generateLinks` instead.
   */
  onError?: RequestHandler;
  /**
   * @deprecated Please use `generateLinks` instead.
   */
  link?: ApolloLink;
  generateLinks?: (
    links: (ApolloLink | RequestHandler)[],
  ) => (ApolloLink | RequestHandler)[];
};

// Simple endpoint configuration
const ENDPOINTS = {
  main: {
    url: process.env.REACT_APP_GRAPHQL_URL || '',
    headers: {} // Access token will be added dynamically by getAuthHeaders()
  },
  secondary: {
    url: process.env.REACT_APP_GRAPHQL_URL_SECONDARY || 'https://ddn-dev.kaktu.al/graphql',
    headers: {} // Access token will be added dynamically by getAuthHeaders()
  }
} as const;

export function selectHasuraRole(allowedRoles: string[]): "admin" | "SEO_ADMIN" | undefined {
  if (allowedRoles.includes("admin")) return "admin";
  if (allowedRoles.includes("SEO_ADMIN")) return "SEO_ADMIN";
  return undefined;
}

// Simple function to create client for specific endpoint
export function createApolloClientForEndpoint(
  auth: KaktuAuthClient, 
  endpoint: keyof typeof ENDPOINTS = 'main'
) {
  const endpointConfig = ENDPOINTS[endpoint];
  
  return createApolloClient({
    auth,
    graphqlUrl: endpointConfig.url,
    headers: endpointConfig.headers
  });
}

export const createApolloClient = ({
  auth,
  graphqlUrl,
  headers = {},
  fetchPolicy,
  cache = new InMemoryCache(),
  connectToDevTools = isBrowser && process.env.NODE_ENV === 'development',
  link: customLink,
  onError,
  generateLinks,
}: KaktuApolloClientOptions) => {
  const backendUrl = graphqlUrl;

  if (!backendUrl) {
    throw Error(
      "Can't initialize the Apollo Client: no backend Url has been provided",
    );
  }

  const uri = backendUrl;
  const interpreter = auth?.client.interpreter;

  let allowedRoles: string[] = auth?.client.interpreter?.state?.context?.user?.roles || [];
  let accessToken: {
    value: string | null;
    expiresAt: Date | null;
    expiresInSeconds: number | null;
  } = auth?.client.interpreter?.state?.context?.accessToken || {value: null, expiresAt: null, expiresInSeconds: null};

  const isJwtValid = () => {
    if (!accessToken?.value) {
      return false;
    }

    const marginInSeconds = 3;
    const marginInMilliseconds = marginInSeconds * 1000;

    const decodedToken = jwtDecode(accessToken.value) as JwtPayload;
    return decodedToken.exp! * 1000 > Date.now() - marginInMilliseconds;
  };

  const isTokenValid = () => accessToken && !!accessToken.value
    && !!accessToken.expiresAt
    && accessToken.expiresAt > new Date()
    && isJwtValid();

  const isTokenValidOrNull = () => !accessToken || isTokenValid();

  const awaitValidTokenOrNull = () => {
    if (isTokenValidOrNull()) {
      return Promise.resolve();
    }

    const waitForValidToken = () => {
      if (isTokenValidOrNull()) {
        return Promise.resolve(true);
      }
      return new Promise((resolve) => {
        setTimeout(() => waitForValidToken().then(resolve), 100);
      });
    };

    return waitForValidToken();
  };


  const getAuthHeaders = async () => {
    // wait for valid access token
    await awaitValidTokenOrNull();

    // add headers
    const resHeaders = {
      ...headers,
      "Sec-WebSocket-Protocol": "graphql-ws",
    };

    // add auth headers if signed in
    // or add 'public' role if not signed in
    if (accessToken) {
      resHeaders.authorization = `Bearer ${accessToken.value}`;
    }
    const selectedRole = selectHasuraRole(allowedRoles);
    if (selectedRole) resHeaders['X-Hasura-Role'] = selectedRole;

    return resHeaders;
  };

  const wsClient = isBrowser
    ? createRestartableClient({
      url: uri.startsWith('https')
        ? uri.replace(/^https/, 'wss')
        : uri.replace(/^http/, 'ws'),
      shouldRetry: () => true,
      retryAttempts: 100,
      retryWait: async (retries: any) => {
        // start with 1 second delay
        const baseDelay = 1000;

        // max 3 seconds of jitter
        const maxJitter = 3000;

        // exponential backoff with jitter
        return new Promise((resolve) => {
          setTimeout(
            resolve,
            baseDelay * 2 ** retries + Math.floor(Math.random() * maxJitter),
          );
        });
      },
      connectionParams: async () => ({
        headers: {
          ...headers,
          ...(await getAuthHeaders()),
        },
      }),
    })
    : null;

  const wsLink = wsClient ? new GraphQLWsLink(wsClient) : null;

  const httpLink = setContext(async (_, ctx) => ({
    headers: {
      ...ctx?.headers,
      ...(await getAuthHeaders()),
    },
  })).concat(createHttpLink({ uri }));

  const splitLink = wsLink
    ? split(
      ({ query }) => {
        const mainDefinition = getMainDefinition(query);

        const { kind } = mainDefinition;
        let operation;
        if ('operation' in mainDefinition) {
          operation = mainDefinition.operation;
        }

        return kind === 'OperationDefinition' && operation === 'subscription';
      },
      wsLink,
      httpLink,
    )
    : httpLink;

  const links = [];

  if (onError) {
    links.push(onError);
  }

  if (customLink) {
    links.push(customLink);
  }

  links.push(splitLink);

  const link = from(generateLinks ? generateLinks(links) : links);

  const client = new ApolloClient({
    cache: cache || new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy,
      },
    },
    connectToDevTools,
    link,
  });

  interpreter?.onTransition(async (state, event) => {
    if (['SIGNOUT', 'SIGNED_IN', 'TOKEN_CHANGED'].includes(event.type)) {
      if (
        event.type === 'SIGNOUT'
        || (event.type === 'TOKEN_CHANGED'
        && state.context.accessToken.value === null)
      ) {
        accessToken = { value: null, expiresAt: null, expiresInSeconds: null };
        allowedRoles = [];

        try {
          await client.clearStore();
        } catch (error) {
          console.error('Error resetting Apollo client cache');
          console.error(error);
        }

        return;
      }

      // update token
      accessToken = state.context.accessToken;
      allowedRoles = state.context.user?.roles || [];

      if (!isBrowser || !wsClient?.isOpen()) {
        return;
      }

      wsClient?.restart();
    }
  });

  return client;
};
